<div class="container mx-auto p-6">
  <h1 class="text-2xl font-bold mb-6">{{ articleId ? ('PIGLET.EDIT_ARTICLE.EDIT_TITLE' | translate) : ('PIGLET.EDIT_ARTICLE.CREATE_TITLE' | translate) }}</h1>

  <!-- Loading indicator -->
  <div *ngIf="loading" class="flex justify-center my-8">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
  </div>

  <!-- Error message -->
  <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
    {{ error }}
  </div>

  <!-- Article form -->
  <form [formGroup]="articleForm" (ngSubmit)="onSubmit()" *ngIf="!loading" class="space-y-6">
    <!-- Basic information section -->
    <div class="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 class="text-xl font-semibold mb-4">{{ 'PIGLET.EDIT_ARTICLE.BASIC_INFO' | translate }}</h2>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Article ID -->
        <div class="form-group">
          <label for="articleId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.ARTICLE_ID' | translate }}</label>
          <input
            type="text"
            id="articleId"
            formControlName="articleId"
            class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
            readonly
          >
        </div>

        <!-- Article Name -->
        <div class="form-group">
          <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.ARTICLE_NAME' | translate }}</label>
          <input
            type="text"
            id="name"
            formControlName="name"
            class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
          >
          <div *ngIf="articleForm.get('name')?.invalid && articleForm.get('name')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.ARTICLE_NAME_REQUIRED' | translate }}
          </div>
        </div>

        <!-- Category Type -->
        <div class="form-group">
          <label for="categoryTypeId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.CATEGORY_TYPE' | translate }}</label>
          <select
            id="categoryTypeId"
            formControlName="categoryTypeId"
            (change)="onCategoryTypeChange()"
            class="form-select w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
          >
            <option [ngValue]="null">{{ 'PIGLET.EDIT_ARTICLE.SELECT_CATEGORY_TYPE' | translate }}</option>
            <option *ngFor="let type of categoryTypes" [value]="type.categoryId">{{ type.categoryType }}</option>
          </select>
          <div *ngIf="articleForm.get('categoryTypeId')?.invalid && articleForm.get('categoryTypeId')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.CATEGORY_TYPE_REQUIRED' | translate }}
          </div>
        </div>

        <!-- Category -->
        <div class="form-group">
          <label for="categoryId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.CATEGORY' | translate }}</label>
          <select
            id="categoryId"
            formControlName="categoryId"
            class="form-select w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
            [disabled]="!articleForm.get('categoryTypeId')?.value || categories.length === 0"
          >
            <option [ngValue]="null">{{ 'PIGLET.EDIT_ARTICLE.SELECT_CATEGORY' | translate }}</option>
            <option *ngFor="let category of categories" [value]="category.categoryId">{{ category.category }}</option>
          </select>
          <div *ngIf="articleForm.get('categoryId')?.invalid && articleForm.get('categoryId')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.CATEGORY_REQUIRED' | translate }}
          </div>
        </div>
      </div>
    </div>

    <!-- Language and Author section -->
    <div class="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 class="text-xl font-semibold mb-4">{{ 'PIGLET.EDIT_ARTICLE.LANGUAGE_AUTHOR_INFO' | translate }}</h2>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Language -->
        <div class="form-group">
          <label for="language" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.LANGUAGE' | translate }}</label>
          <select
            id="language"
            formControlName="language"
            (change)="onLanguageChange()"
            class="form-select w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
          >
            <optgroup [label]="'PIGLET.EDIT_ARTICLE.AVAILABLE_LANGUAGES' | translate" *ngIf="availableLanguages.length > 0">
              <option *ngFor="let lang of availableLanguages" [value]="lang">{{ lang }}</option>
            </optgroup>
            <optgroup [label]="'PIGLET.EDIT_ARTICLE.OTHER_LANGUAGES' | translate">
              <option *ngFor="let lang of allLanguages" [value]="lang" [hidden]="availableLanguages.includes(lang)">{{ lang }}</option>
            </optgroup>
          </select>
          <div *ngIf="articleForm.get('language')?.invalid && articleForm.get('language')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.LANGUAGE_REQUIRED' | translate }}
          </div>
        </div>

        <!-- Author -->
        <div class="form-group">
          <label for="author" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.AUTHOR' | translate }}</label>
          <input
            type="text"
            id="author"
            formControlName="author"
            class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
            readonly
          >
        </div>

        <!-- Publication Date -->
        <div class="form-group">
          <label for="publishDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.PUBLICATION_DATE' | translate }}</label>
          <input
            type="datetime-local"
            id="publishDate"
            formControlName="publishDate"
            class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
          >
        </div>
      </div>
    </div>

    <!-- Article Settings section -->
    <div class="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 class="text-xl font-semibold mb-4">{{ 'PIGLET.EDIT_ARTICLE.ARTICLE_SETTINGS' | translate }}</h2>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Allow Comments -->
        <div class="form-group">
          <div class="flex items-center">
            <input
              type="checkbox"
              id="allowComments"
              formControlName="allowComments"
              class="form-checkbox h-5 w-5 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
            >
            <label for="allowComments" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'PIGLET.EDIT_ARTICLE.ALLOW_COMMENTS' | translate }}
            </label>
          </div>
        </div>

        <!-- Published -->
        <div class="form-group">
          <div class="flex items-center">
            <input
              type="checkbox"
              id="published"
              formControlName="published"
              class="form-checkbox h-5 w-5 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
            >
            <label for="published" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'PIGLET.EDIT_ARTICLE.PUBLISHED' | translate }}
            </label>
          </div>
        </div>

        <!-- Visible on Lists -->
        <div class="form-group">
          <div class="flex items-center">
            <input
              type="checkbox"
              id="visible"
              formControlName="visible"
              class="form-checkbox h-5 w-5 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
            >
            <label for="visible" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ 'PIGLET.EDIT_ARTICLE.VISIBLE_ON_LISTS' | translate }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Article Content section -->
    <div class="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
      <h2 class="text-xl font-semibold mb-4">{{ 'PIGLET.EDIT_ARTICLE.ARTICLE_CONTENT' | translate }}</h2>

      <div class="space-y-6">
        <!-- Title -->
        <div class="form-group">
          <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.TITLE' | translate }}</label>
          <input
            type="text"
            id="title"
            formControlName="title"
            class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
          >
          <div *ngIf="articleForm.get('title')?.invalid && articleForm.get('title')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.TITLE_REQUIRED' | translate }}
          </div>
        </div>

        <!-- Content -->
        <div class="form-group">
          <label for="content" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.CONTENT' | translate }}</label>
          <div class="main-container">
            <div
              class="editor-container editor-container_classic-editor editor-container_include-style editor-container_include-word-count editor-container_include-fullscreen"
              #editorContainerElement
            >
              <div class="editor-container__editor">
                <div #editorElement>
                  <ckeditor [editor]="Editor" [config]="config" (ready)="onReady($event)" *ngIf="Editor && config" formControlName="content" />
                </div>
              </div>
              <div class="editor_container__word-count" #editorWordCountElement></div>
            </div>
          </div>
          <div *ngIf="articleForm.get('content')?.invalid && articleForm.get('content')?.touched" class="text-red-500 mt-1 text-sm">
            {{ 'PIGLET.EDIT_ARTICLE.CONTENT_REQUIRED' | translate }}
          </div>
        </div>

        <!-- Image fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Thumbnail Image -->
          <div class="form-group">
            <label for="thumbnailImage" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.THUMBNAIL_IMAGE' | translate }}</label>
            <input
              type="text"
              id="thumbnailImage"
              formControlName="thumbnailImage"
              class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
              [placeholder]="'PIGLET.EDIT_ARTICLE.ENTER_IMAGE_URL' | translate"
            >
          </div>

          <!-- Background Image -->
          <div class="form-group">
            <label for="backgroundImage" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ 'PIGLET.EDIT_ARTICLE.BACKGROUND_IMAGE' | translate }}</label>
            <input
              type="text"
              id="backgroundImage"
              formControlName="backgroundImage"
              class="form-input w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-200 focus:border-violet-600 dark:border-gray-800 dark:focus:border-violet-600 focus:ring-0"
              [placeholder]="'PIGLET.EDIT_ARTICLE.ENTER_IMAGE_URL' | translate"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="flex justify-end">
      <button
        type="submit"
        [disabled]="articleForm.invalid || submitting"
        class="btn bg-violet-600 hover:bg-violet-700 border-violet-600 hover:border-violet-700 text-white rounded-md px-6 py-2 flex items-center justify-center"
        [ngClass]="{'opacity-50 cursor-not-allowed': articleForm.invalid || submitting}"
      >
        <span *ngIf="submitting" class="inline-block mr-2">
          <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
        {{ articleId ? ('PIGLET.EDIT_ARTICLE.UPDATE_ARTICLE' | translate) : ('PIGLET.EDIT_ARTICLE.CREATE_ARTICLE' | translate) }}
      </button>
    </div>
  </form>
</div>
